import { GtmTriggerName } from '@/constants/gtm'
import type { BadgeKind, PageBadge, SectionType, SortType } from './types'

export const Pages: SectionType[] = [
  'total',
  'indexed',
  'skipped',
  'healthy',
  'warning',
  'broken',
]

export const SECTION_PARAM = 'section'

export const CHANGES_PARAM = 'changes'

export const SEARCH_PARAM = 'search'

export const SORT_PARAM = 'sort'

export const DEFAULT_SECTION = 'total' as const

export const DEFAULT_SORT = 'hierarchy' as const

export const ReportPageOpenedEvents: Partial<
  Record<SectionType, GtmTriggerName>
> = {
  broken: GtmTriggerName.REPORT_PAGE_OPENED_BROKEN,
  healthy: GtmTriggerName.REPORT_PAGE_OPENED_HEALTHY,
  indexed: GtmTriggerName.REPORT_PAGE_OPENED_INDEXED,
  skipped: GtmTriggerName.REPORT_PAGE_OPENED_SKIPPED,
  warning: GtmTriggerName.REPORT_PAGE_OPENED_WARNING,
}

export const ReportPageOpenedDeltaEvents: Partial<
  Record<SectionType, GtmTriggerName>
> = {
  broken: GtmTriggerName.REPORT_PAGE_OPENED_BROKEN_DELTA,
  healthy: GtmTriggerName.REPORT_PAGE_OPENED_HEALTHY_DELTA,
  indexed: GtmTriggerName.REPORT_PAGE_OPENED_INDEXED_DELTA,
  skipped: GtmTriggerName.REPORT_PAGE_OPENED_SKIPPED_DELTA,
  warning: GtmTriggerName.REPORT_PAGE_OPENED_WARNING_DELTA,
}

export const SectionTitles: Record<SectionType, string> = {
  total: 'Total pages',
  indexed: 'Indexed pages',
  skipped: 'Skipped pages',
  healthy: 'Healthy pages',
  warning: 'Warning pages',
  broken: 'Broken pages',
}

// The counters say "Healthy pages"; a CSV column headed Section does not need
// the word twice.
export const SectionLabels: Record<SectionType, string> = {
  total: 'Total',
  indexed: 'Indexed',
  skipped: 'Skipped',
  healthy: 'Healthy',
  warning: 'Warning',
  broken: 'Broken',
}

export const Sorts: SortType[] = ['hierarchy', 'changes', 'problems']

export const SortTitles: Record<SortType, string> = {
  hierarchy: 'Hierarchy',
  changes: 'Changes first',
  problems: 'Problems first',
}

// Colour and wording for every badge the API can send. A redirect is a page
// that still answers but is no longer itself, so it reads as a warning, not a
// failure. Blocked is not danger either: the site turned the crawler away, which
// says nothing about what a visitor sees.
export const Badges: Record<BadgeKind, PageBadge> = {
  blocked: { kind: 'blocked', type: 'warning', title: 'Blocked' },
  broken: { kind: 'broken', type: 'danger', title: 'Broken' },
  missing_description: {
    kind: 'missing_description',
    type: 'outline',
    title: 'No description',
  },
  missing_h1: { kind: 'missing_h1', type: 'warning', title: 'Missing H1' },
  missing_title: { kind: 'missing_title', type: 'warning', title: 'No title' },
  new: { kind: 'new', type: 'info', title: 'New' },
  nofollow: {
    kind: 'nofollow',
    type: 'outline',
    title: 'Not crawled: nofollow',
  },
  noindex: { kind: 'noindex', type: 'outline', title: 'Noindex' },
  redirect: { kind: 'redirect', type: 'warning', title: 'Redirect' },
  removed: { kind: 'removed', type: 'danger', title: 'Removed' },
  robots: { kind: 'robots', type: 'outline', title: 'Not crawled: robots.txt' },
  skipped: { kind: 'skipped', type: 'outline', title: 'Skipped' },
  updated: { kind: 'updated', type: 'info', title: 'Updated' },
}

// Badges that read better with the code that caused them: "Broken: 404" says
// which failure it was, and "Blocked: 429" says the site is rate limiting us
// rather than refusing outright.
export const BadgesWithStatus: BadgeKind[] = ['blocked', 'broken']
